/**
 * v-image-preview 指令
 * 鼠标悬浮在图片上时，在光标附近显示放大预览浮层。
 *
 * 用法：
 *   <img v-image-preview :src="thumbUrl" />
 *   <img v-image-preview="fullImageUrl" :src="thumbUrl" />
 *   <div v-image-preview="imageUrl"> ... </div>
 *
 * 传入值：
 *   - 不传值 / 传空：使用元素自身 src 属性（去掉缩略图参数）作为预览大图
 *   - 传字符串 URL：使用该 URL 作为预览大图
 */

// 单例浮层 DOM
let overlay = null
let overlayImg = null
let hideTimer = null
let currentTarget = null

const PREVIEW_MAX_W = 520
const PREVIEW_MAX_H = 620
const OFFSET_X = 18
const OFFSET_Y = 18

function ensureOverlay() {
  if (overlay) return
  overlay = document.createElement('div')
  overlay.className = 'img-hover-preview-overlay'
  Object.assign(overlay.style, {
    position: 'fixed',
    zIndex: '99999',
    pointerEvents: 'none',
    opacity: '0',
    transition: 'opacity 0.15s ease',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.12)',
    background: '#fff',
    maxWidth: PREVIEW_MAX_W + 'px',
    maxHeight: PREVIEW_MAX_H + 'px',
  })

  overlayImg = document.createElement('img')
  Object.assign(overlayImg.style, {
    display: 'block',
    maxWidth: PREVIEW_MAX_W + 'px',
    maxHeight: PREVIEW_MAX_H + 'px',
    objectFit: 'contain',
    borderRadius: '10px',
  })
  overlay.appendChild(overlayImg)
  document.body.appendChild(overlay)
}

function showPreview(imgUrl, x, y) {
  ensureOverlay()
  if (hideTimer) { clearTimeout(hideTimer); hideTimer = null }

  overlayImg.src = imgUrl
  overlay.style.opacity = '1'
  positionOverlay(x, y)
}

function positionOverlay(x, y) {
  if (!overlay) return
  const vw = window.innerWidth
  const vh = window.innerHeight
  const ow = overlay.offsetWidth || PREVIEW_MAX_W
  const oh = overlay.offsetHeight || PREVIEW_MAX_H

  let left = x + OFFSET_X
  let top = y + OFFSET_Y

  // 右边放不下则移到左边
  if (left + ow > vw - 8) {
    left = x - ow - OFFSET_X
  }
  // 下边放不下则移到上边
  if (top + oh > vh - 8) {
    top = y - oh - OFFSET_Y
  }
  // 确保不超出左边和上边
  if (left < 8) left = 8
  if (top < 8) top = 8

  overlay.style.left = left + 'px'
  overlay.style.top = top + 'px'
}

function hidePreview() {
  if (!overlay) return
  hideTimer = setTimeout(() => {
    if (overlay) overlay.style.opacity = '0'
  }, 80)
}

/**
 * 从缩略 URL 还原为高清大图 URL
 * 去掉七牛 imageView2 参数，或使用更高分辨率
 */
function getFullUrl(url) {
  if (!url) return ''
  // 移除七牛缩略图参数
  const idx = url.indexOf('?imageView2/')
  if (idx !== -1) {
    return url.substring(0, idx)
  }
  // 也处理 &imageView2/ 的情况
  const idx2 = url.indexOf('&imageView2/')
  if (idx2 !== -1) {
    return url.substring(0, idx2)
  }
  return url
}

function getImageUrl(el, binding) {
  // 显式传入空字符串表示禁用预览
  if (binding.value === '') {
    return ''
  }
  // 优先使用 binding.value
  if (binding.value) {
    return typeof binding.value === 'string' ? binding.value : ''
  }
  // 否则使用元素自身 src
  const src = el.tagName === 'IMG' ? el.src : (el.querySelector('img') || {}).src
  return getFullUrl(src || '')
}

const imagePreviewDirective = {
  bind(el, binding) {
    const onMouseEnter = (e) => {
      const url = getImageUrl(el, binding)
      if (!url) return
      currentTarget = el
      showPreview(url, e.clientX, e.clientY)
    }
    const onMouseMove = (e) => {
      if (currentTarget !== el) return
      positionOverlay(e.clientX, e.clientY)
    }
    const onMouseLeave = () => {
      if (currentTarget === el) {
        currentTarget = null
        hidePreview()
      }
    }

    el._imgPreviewHandlers = { onMouseEnter, onMouseMove, onMouseLeave }
    el.addEventListener('mouseenter', onMouseEnter)
    el.addEventListener('mousemove', onMouseMove)
    el.addEventListener('mouseleave', onMouseLeave)
  },
  update(el, binding) {
    // 更新时替换 handler 以使用新的 URL
    if (el._imgPreviewHandlers) {
      el.removeEventListener('mouseenter', el._imgPreviewHandlers.onMouseEnter)
      el.removeEventListener('mousemove', el._imgPreviewHandlers.onMouseMove)
      el.removeEventListener('mouseleave', el._imgPreviewHandlers.onMouseLeave)
    }
    const onMouseEnter = (e) => {
      const url = getImageUrl(el, binding)
      if (!url) return
      currentTarget = el
      showPreview(url, e.clientX, e.clientY)
    }
    const onMouseMove = (e) => {
      if (currentTarget !== el) return
      positionOverlay(e.clientX, e.clientY)
    }
    const onMouseLeave = () => {
      if (currentTarget === el) {
        currentTarget = null
        hidePreview()
      }
    }
    el._imgPreviewHandlers = { onMouseEnter, onMouseMove, onMouseLeave }
    el.addEventListener('mouseenter', onMouseEnter)
    el.addEventListener('mousemove', onMouseMove)
    el.addEventListener('mouseleave', onMouseLeave)
  },
  unbind(el) {
    if (el._imgPreviewHandlers) {
      el.removeEventListener('mouseenter', el._imgPreviewHandlers.onMouseEnter)
      el.removeEventListener('mousemove', el._imgPreviewHandlers.onMouseMove)
      el.removeEventListener('mouseleave', el._imgPreviewHandlers.onMouseLeave)
      delete el._imgPreviewHandlers
    }
    if (currentTarget === el) {
      currentTarget = null
      hidePreview()
    }
  }
}

export default imagePreviewDirective
